import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/web-design-firms-colorado");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/web-design-firms-colorado")({
  head: () => ({
    meta: [
      { title: "Web Design Firms in Colorado: How to Choose the Right One (2026)" },
      { name: "description", content: "Comparing web design firms? The three types of firms, honest Colorado pricing ($1,850–$5,950), and the questions that separate real builders from resellers." },
      { property: "og:title", content: "Web Design Firms in Colorado: How to Choose the Right One" },
      { property: "og:description", content: "The three types of web design firms, honest Colorado pricing, and the questions that expose resellers." },
      { property: "og:url", content: `${SITE_URL}/learn/web-design-firms-colorado` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Web design firms compared",
        description: ARTICLE?.desc ?? "How to choose between web design firms in Colorado.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/web-design-firms-colorado" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Web Design"
      read="9 min read"
      title="Web design firms in Colorado: how to choose the right one"
      intro="Search for web design firms and you'll get thousands of results that all look identical. Here's how to tell the three types apart, what a fair price looks like in Colorado, and the questions that expose a firm that's reselling someone else's work."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Web design firms" },
      ]}
      path="/learn/web-design-firms-colorado"
      faqs={[
        { q: "How much do web design firms charge in Colorado?", a: "For a small business site, honest Colorado pricing runs $1,850 for a 4-page starter site up to about $5,950 for a large 50-page build. Firms quoting $15,000+ for a basic local business site are charging for their overhead, not your results." },
        { q: "What's the difference between a web design firm and a freelancer?", a: "A freelancer is one person — cheaper, but everything stops if they get busy or disappear. A firm has a process and backup. The trade-off is some 'firms' are one salesperson outsourcing overseas. Ask who actually builds your site." },
        { q: "Should a web design firm also do SEO?", a: "Ideally yes. A beautiful site that can't rank is an expensive brochure. Firms that build with search in mind — page structure, speed, schema — save you from paying twice." },
      ]}
    >
      <h2>The three types of web design firms</h2>
      <p>Every firm you'll talk to is one of these, whether they admit it or not:</p>
      <ul>
        <li><strong>The template shop.</strong> Fast and cheap. Your site is a pre-made theme with your logo dropped in. Fine for a hobby, weak for a business that needs calls.</li>
        <li><strong>The big agency.</strong> Polished proposals, account managers, and a junior designer you'll never meet doing the actual work. You pay for the office and the overhead.</li>
        <li><strong>The specialist.</strong> A small team that builds for a specific kind of client — like Colorado service businesses — and can show you real results in your industry.</li>
      </ul>

      <h2>Honest Colorado pricing</h2>
      <p>
        Our published packages are a fair benchmark: <strong>$1,850</strong> for a 4-page
        SEO-ready starter site, <strong>$2,750</strong> for a 10-page site with foundation SEO
        and analytics done for you, and <strong>$5,950</strong> for up to 50 pages with
        service-by-city architecture built to rank. All include a year of hosting, SSL, and a
        traffic dashboard, with most sites live in under 7 business days. See the full breakdown
        on our <Link to="/pricing">pricing page</Link>.
      </p>

      <h2>Questions that expose resellers</h2>
      <ul>
        <li><strong>Who builds my site, and can I talk to them?</strong> If the answer is a project manager relaying messages, your site is being outsourced.</li>
        <li><strong>Do I own the site if I leave?</strong> Your domain, content, and files should be yours. If leaving means losing everything, you were renting.</li>
        <li><strong>What happens after launch?</strong> A site isn't a one-time purchase. Ask what edits, hosting, and support cost in month two.</li>
        <li><strong>Show me a client in my industry who ranks.</strong> Pretty portfolios are easy. Page-one rankings for a real client are proof.</li>
      </ul>

      <h2>Why local matters more than you think</h2>
      <p>
        A Colorado firm understands your market — the cities you serve, the seasonality of
        trades work, what your customers search. An out-of-state template shop doesn't know
        Colorado Springs from Castle Rock, and your site will read like it. See how we approach{" "}
        <Link to="/colorado-web-design">web design across Colorado</Link>.
      </p>

      <h2>What to do next</h2>
      <p>
        Want to see what your new site could look like before spending anything? Request a{" "}
        <Link to="/free-mockup">free website mockup</Link> — no obligation, no contract. Or
        read our guide on <Link to="/learn/companies-that-build-websites-colorado">companies that build websites</Link>{" "}
        for the full buyer's checklist.
      </p>
    </ArticleLayout>
  );
}
